import { deckOrder, GetStartedDeck, openTourChecklist, readProgress, TourPicture, useTourChecklistHome, withDeckHidden, withStepSkipped } from '@flobi/asset-library/tours';
import '@flobi/asset-library/tours.css';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../theme';
import { usePhone } from '../../ui/usePhone';
import { loadSteps, saveViewPref, useOnboarding } from './tourData';
import { PROGRESS_KEY } from './tours';
import { TOUR_ANCHORS } from './tourAnchors';
import { pictureForStep } from './pictureScenes';

const noTour = () => null;

/**
 * Get started on Home, between the prompt box and Recent. On a computer it is the lessons card in the
 * corner (the kit's TourChecklist, mounted by ArtworkTourHost): while Home is here the card opens by
 * itself, until every step is done or it's closed (Mohamad, 30 Oct: Flow's checklist in every app, in
 * place of the "New to Artwork?" card and its Learn Artwork row). On a phone it is the kit's "New to
 * Artwork?" deck, with the next tour's own picture on its band, where a card's button opens its page:
 * no rings there yet. Its × is Artwork's own (artworkTours.hidden). The steps are only read for someone
 * signed in (ArtworkTourHost), so none means nothing to show. `on` is false while Home shows a
 * search instead: the card doesn't open by itself over the results.
 */
export function ArtworkGetStarted({ on = true }: { on?: boolean }) {
  const phone = usePhone();
  return phone ? <Deck /> : <AtHome on={on} />;
}

/**
 * The first page (FirstVisit, while the workspace has no files) is Home too: on a computer the lessons
 * card opens there by itself, for the person who has made nothing yet. A phone's first page stays as it is.
 */
export function ArtworkHomeMark() {
  const phone = usePhone();
  return phone ? null : <AtHome on />;
}

/** Home is on screen: the lessons card may open by itself, with the steps read again. */
function AtHome({ on }: { on: boolean }) {
  useEffect(() => {
    if (on) void loadSteps();
  }, [on]);
  useTourChecklistHome(on);
  return null;
}

function Deck() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { steps, viewPrefs } = useOnboarding();
  const progress = readProgress(viewPrefs, PROGRESS_KEY);

  // Read again on each visit, so a step ticked elsewhere (the gateway's trigger routes) shows here.
  useEffect(() => {
    void loadSteps();
  }, []);

  if (!steps.length || steps.every((s) => s.completed)) return null;
  const next = deckOrder(steps, progress.skippedSteps)[0];
  const art = pictureForStep(next?.action_key);
  return (
    <section aria-label="Learn Artwork" className="mb-[48px]">
      <GetStartedDeck
        app="artwork"
        mode={theme === 'light' ? 'light' : 'dark'}
        steps={steps}
        skippedSteps={progress.skippedSteps}
        tourForStep={noTour}
        onRoute={(path) => navigate(path)}
        onSkip={(id) => void saveViewPref(PROGRESS_KEY, withStepSkipped(progress, id))}
        onDismiss={() => void saveViewPref(PROGRESS_KEY, withDeckHidden(progress))}
        anchor={TOUR_ANCHORS.getStarted}
        appName="Artwork"
        art={art ? <TourPicture html={art} /> : null}
        dismissed={Boolean(progress.hidden)}
      />
    </section>
  );
}

/**
 * The way back in: the title menu's "Learn Artwork · 2 of 6" opens the lessons card in the corner (the
 * kit's TourChecklist), where the next step is raised. On a phone it opens the next step's page and
 * brings back a deck closed with its ×. Null once every step is done.
 */
export function useGetStarted(): { label: string; count: string; hidden: boolean; go: () => void } | null {
  const navigate = useNavigate();
  const phone = usePhone();
  const { steps, viewPrefs } = useOnboarding();
  if (!steps.length) return null;
  const progress = readProgress(viewPrefs, PROGRESS_KEY);
  const next = deckOrder(steps, progress.skippedSteps)[0];
  if (!next) return null;
  const done = steps.filter((s) => s.completed).length;
  return {
    label: 'Learn Artwork',
    count: `${done} of ${steps.length}`,
    hidden: Boolean(progress.hidden),
    go: () => {
      if (!phone) return openTourChecklist();
      if (progress.hidden) void saveViewPref(PROGRESS_KEY, withDeckHidden(progress, false));
      navigate(next.frontend_route || '/');
    },
  };
}
